/**
 * AI Financial Advisor Service
 * 
 * Integrates with Gemini API to provide personalized financial advice
 * with full context of the user's financial situation.
 */

import type { ChatFinancialContext, ChatMessage } from '@/types'
import { AI_SYSTEM_PROMPT } from '@/types/chat.types'
import { formatContextForPrompt } from '@/hooks/useFinancialContext'

// API configuration
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent'

interface GeminiMessage {
  role: 'user' | 'model'
  parts: { text: string }[]
}

interface GeminiResponse {
  candidates?: Array<{
    content: {
      parts: Array<{ text: string }>
      role: string
    }
    finishReason: string
  }>
  error?: {
    message: string
    code: number
  }
}

/**
 * Build the system prompt with injected financial context
 */
function buildSystemPrompt(context: ChatFinancialContext): string {
  const contextJson = formatContextForPrompt(context)
  return AI_SYSTEM_PROMPT.replace('{CONTEXT_JSON}', contextJson)
}

/**
 * Convert chat history to Gemini format
 */
function convertToGeminiMessages(
  messages: ChatMessage[],
  context: ChatFinancialContext
): GeminiMessage[] {
  const geminiMessages: GeminiMessage[] = []
  
  // Add system context as first user message (Gemini doesn't have a system role)
  const systemPrompt = buildSystemPrompt(context)
  
  // Process conversation messages
  messages.forEach((msg, index) => {
    if (msg.role === 'user') {
      // For the first user message, prepend the system context
      if (index === 0 || geminiMessages.length === 0) {
        geminiMessages.push({
          role: 'user',
          parts: [{ text: `${systemPrompt}\n\nUser question: ${msg.content}` }],
        })
      } else {
        geminiMessages.push({
          role: 'user',
          parts: [{ text: msg.content }],
        })
      }
    } else if (msg.role === 'assistant') {
      geminiMessages.push({
        role: 'model',
        parts: [{ text: msg.content }],
      })
    }
  })
  
  return geminiMessages
}

/**
 * Send a message to the AI advisor and get a response
 */
export async function sendMessage(
  userMessage: string,
  conversationHistory: ChatMessage[],
  context: ChatFinancialContext,
  apiKey: string
): Promise<string> {
  if (!apiKey) {
    throw new Error('Gemini API key is required. Please add your API key in settings.')
  }
  
  // Build the current conversation with the new message
  const currentMessages: ChatMessage[] = [
    ...conversationHistory,
    {
      id: 'temp',
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
      status: 'sent',
    },
  ]
  
  // Convert to Gemini format
  const geminiMessages = convertToGeminiMessages(currentMessages, context)
  
  // Make API request
  const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: geminiMessages,
      generationConfig: {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 2048,
      },
      safetySettings: [
        {
          category: 'HARM_CATEGORY_HARASSMENT',
          threshold: 'BLOCK_MEDIUM_AND_ABOVE',
        },
        {
          category: 'HARM_CATEGORY_HATE_SPEECH',
          threshold: 'BLOCK_MEDIUM_AND_ABOVE',
        },
        {
          category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT',
          threshold: 'BLOCK_MEDIUM_AND_ABOVE',
        },
        {
          category: 'HARM_CATEGORY_DANGEROUS_CONTENT',
          threshold: 'BLOCK_MEDIUM_AND_ABOVE',
        },
      ],
    }),
  })
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error?.message || `API request failed with status ${response.status}`
    )
  }
  
  const data: GeminiResponse = await response.json()
  
  if (data.error) {
    throw new Error(data.error.message)
  }
  
  if (!data.candidates || data.candidates.length === 0) {
    throw new Error('No response generated. Please try again.')
  }
  
  const candidate = data.candidates[0]
  
  if (candidate.finishReason === 'SAFETY') {
    throw new Error('Response was blocked due to safety filters. Please rephrase your question.')
  }
  
  const responseText = candidate.content.parts
    .map(part => part.text)
    .join('')
  
  return responseText
}

/**
 * Stream a message response (if Gemini supports it in the future)
 * For now, this just wraps sendMessage
 */
export async function streamMessage(
  userMessage: string,
  conversationHistory: ChatMessage[],
  context: ChatFinancialContext,
  apiKey: string,
  onChunk: (chunk: string) => void
): Promise<void> {
  // For now, just get the full response and call onChunk once
  const response = await sendMessage(userMessage, conversationHistory, context, apiKey)
  onChunk(response)
}

/**
 * Generate a quick insight based on the financial context
 * Used for dashboard widgets and quick tips
 */
export async function generateQuickInsight(
  context: ChatFinancialContext,
  apiKey: string
): Promise<string> {
  const prompt = `Based on this financial profile, give me ONE specific, actionable tip in 2-3 sentences. Focus on the most impactful thing they could do to improve their finances.

Profile summary:
- Age: ${context.personal.age}, retiring at ${context.personal.retirementAge}
- Net worth: $${context.finances.netWorth.toLocaleString()}
- Savings rate: ${context.finances.savingsRate}%
- Emergency fund: ${context.finances.emergencyFundMonths} months
- Retirement readiness: ${context.projections.retirementReadinessScore}%
- Biggest risk: ${context.risks.biggestRisk}

Give a specific tip (not generic advice):`;

  const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [{
        role: 'user',
        parts: [{ text: prompt }],
      }],
      generationConfig: {
        temperature: 0.7,
        maxOutputTokens: 256,
      },
    }),
  })
  
  if (!response.ok) {
    throw new Error('Failed to generate insight')
  }
  
  const data: GeminiResponse = await response.json()
  
  if (!data.candidates || data.candidates.length === 0) {
    throw new Error('No insight generated')
  }
  
  return data.candidates[0].content.parts.map(p => p.text).join('')
}

/**
 * Predict wage growth for an industry
 */
export async function predictWageGrowth(
  industry: string,
  currentSalary: number,
  yearsExperience: number,
  apiKey: string
): Promise<{
  annualGrowthRate: number
  fiveYearProjection: number
  tenYearProjection: number
  confidence: 'high' | 'medium' | 'low'
  factors: string[]
}> {
  const prompt = `You are a labor market analyst. For someone working in "${industry}" with ${yearsExperience} years of experience and a current salary of $${currentSalary.toLocaleString()}, predict their salary growth.

Return ONLY a JSON object (no markdown, no explanation) with:
{
  "annualGrowthRate": number (percentage, e.g., 3.5),
  "fiveYearProjection": number (salary in 5 years),
  "tenYearProjection": number (salary in 10 years),
  "confidence": "high" | "medium" | "low",
  "factors": [array of 2-3 key factors affecting this industry's wages]
}`;

  const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [{
        role: 'user',
        parts: [{ text: prompt }],
      }],
      generationConfig: {
        temperature: 0.3,
        maxOutputTokens: 512,
      },
    }),
  })
  
  if (!response.ok) {
    // Return default values if API fails
    return {
      annualGrowthRate: 3,
      fiveYearProjection: currentSalary * Math.pow(1.03, 5),
      tenYearProjection: currentSalary * Math.pow(1.03, 10),
      confidence: 'low',
      factors: ['Unable to fetch industry data'],
    }
  }
  
  const data: GeminiResponse = await response.json()
  
  try {
    const text = data.candidates?.[0]?.content.parts.map(p => p.text).join('') || ''
    // Try to parse JSON from the response
    const jsonMatch = text.match(/\{[\s\S]*\}/)
    if (jsonMatch) {
      return JSON.parse(jsonMatch[0])
    }
  } catch (e) {
    // Return defaults on parse error
  }
  
  return {
    annualGrowthRate: 3,
    fiveYearProjection: currentSalary * Math.pow(1.03, 5),
    tenYearProjection: currentSalary * Math.pow(1.03, 10),
    confidence: 'low',
    factors: ['Using default growth estimates'],
  }
}
